/**
 * Format a currency amount. Defaults to CAD with 2 decimal places.
 *
 * @param amount The amount in dollars (or cents if `fromCents` is true)
 * @param opts.decimals Number of decimal places (default 2); 'auto' uses 0 for integers, 2 otherwise
 * @param opts.fromCents If true, divide by 100 to convert cents to dollars
 * @param opts.fallback String to return if amount is null/undefined/NaN/Infinity (default "—")
 * @param opts.currency Currency code (default "CAD")
 * @param opts.locale Locale string (default "en-CA")
 * @param opts.compact If true and amount >= 1000, use compact notation (e.g., "$1.2M")
 * @returns Formatted currency string
 */
export function formatCurrency(
  amount: number | null | undefined,
  opts?: {
    decimals?: number | 'auto'
    fromCents?: boolean
    fallback?: string
    currency?: string
    locale?: string
    compact?: boolean
  }
): string {
  const {
    decimals = 2,
    fromCents = false,
    fallback = "—",
    currency = "CAD",
    locale = "en-CA",
    compact = false
  } = opts || {}

  if (amount == null || !Number.isFinite(amount)) return fallback

  const value = fromCents ? amount / 100 : amount

  // Use compact notation if requested and value is >= 1000
  if (compact && Math.abs(value) >= 1000) {
    // Magnitude-aware precision: 0 decimals under $1M, 1 decimal at $1M+
    // (matches the manual $1.2M / $45k bucket formatting compact call sites had pre-migration)
    const compactFractionDigits = Math.abs(value) >= 1_000_000 ? 1 : 0
    const formatter = new Intl.NumberFormat(locale, {
      style: "currency",
      currency,
      maximumFractionDigits: compactFractionDigits,
      notation: "compact"
    })
    return formatter.format(value)
  }

  // Determine fraction digits for standard notation
  let maxFractionDigits = typeof decimals === 'number' ? decimals : 2
  let minFractionDigits = typeof decimals === 'number' ? decimals : 2

  if (decimals === 'auto') {
    minFractionDigits = value % 1 === 0 ? 0 : 2
    maxFractionDigits = value % 1 === 0 ? 0 : 2
  }

  const formatter = new Intl.NumberFormat(locale, {
    style: "currency",
    currency,
    maximumFractionDigits: maxFractionDigits,
    minimumFractionDigits: minFractionDigits,
  })

  return formatter.format(value)
}
